import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

export const useScrollState = (scrollAmount = 150) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const [hasOverflow, setHasOverflow] = useState(false)
  const [atStart, setAtStart] = useState(true)
  const [atEnd, setAtEnd] = useState(false)

  const updateScrollState = useCallback(() => {
    const scrollEl = scrollRef.current
    const containerEl = containerRef.current
    if (!scrollEl || !containerEl) return

    const { scrollWidth, scrollLeft, clientWidth } = scrollEl
    const visibleWidth = Math.min(containerEl.offsetWidth, window.innerWidth)

    setHasOverflow(scrollWidth > visibleWidth)
    setAtStart(scrollLeft <= 0)
    setAtEnd(scrollLeft + clientWidth >= scrollWidth - 1)
  }, [])

  useEffect(() => {
    updateScrollState()

    const scrollEl = scrollRef.current
    scrollEl?.addEventListener('scroll', updateScrollState)
    window.addEventListener('resize', updateScrollState)

    return () => {
      scrollEl?.removeEventListener('scroll', updateScrollState)
      window.removeEventListener('resize', updateScrollState)
    }
  }, [updateScrollState])

  useLayoutEffect(() => {
    updateScrollState()
  })

  const scrollLeft = () => {
    if (!scrollRef.current) return
    scrollRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' })
  }

  const scrollRight = () => {
    if (!scrollRef.current) return
    scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' })
  }

  return { scrollRef, containerRef, hasOverflow, atStart, atEnd, scrollLeft, scrollRight }
}
